import './policies-entity-grid.scss';

import { Box, IconButton } from '@mui/material';
import {
  CircularLoader,
  ConvergeButton,
  DashboardHeader,
  PaginatedTable,
  PaperLayout,
} from '../../../presentation';
import {
  PoliciesEntityFieldIdType,
  PoliciesEntityFieldLabelType,
} from '@converge/data';
import { toPagination, useGetPoliciesQuery } from '@converge/graphql';

import { ConvergeNavigationUrls } from '../../../contexts';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const PoliciesEntityGrid = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { loading, data, policies } = useGetPoliciesQuery({
    pagination: toPagination(currentPage, rowsPerPage),
  });

  if (loading) {
    return <CircularLoader className="circular-loader-style" />;
  }

  return (
    <>
      <Box className="entity-grid-header-title">
        <DashboardHeader
          title={t('entities.policies.title', 'Policies')}
          children={
            <div className="right-section">
              <IconButton>
                <FilterAltIcon className="filter-style" />
              </IconButton>
            </div>
          }
        />
      </Box>
      <PaperLayout className="entity-grid-layout">
        <PaginatedTable
          columns={[
            {
              id: PoliciesEntityFieldIdType.ReferenceId,
              label: PoliciesEntityFieldLabelType.ReferenceId,
              render: (row) => (
                <Box className="reference-id-column">
                  <Link
                    className="reference-id"
                    to={ConvergeNavigationUrls.policyEntityDetail({
                      policyUuid: row.uuid,
                    })}
                  >
                    {row.referenceId}
                  </Link>
                </Box>
              ),
              isSortable: true,
            },
            {
              id: PoliciesEntityFieldIdType.InsuredName,
              label: PoliciesEntityFieldLabelType.InsuredName,
              render: (row) => row?.insuredName,
              isSortable: true,
            },
            {
              id: PoliciesEntityFieldIdType.InceptionDate,
              label: PoliciesEntityFieldLabelType.InceptionDate,
              render: (row) => row?.startDate,
              isSortable: true,
            },
            {
              id: PoliciesEntityFieldIdType.EndDate,
              label: PoliciesEntityFieldLabelType.EndDate,
              render: (row) => row?.endDate,
              isSortable: true,
            },
            {
              id: PoliciesEntityFieldIdType.Premium,
              label: PoliciesEntityFieldLabelType.Premium,
              render: (row) => row?.netPremium,
              isSortable: true,
            },
            {
              id: PoliciesEntityFieldIdType.Status,
              label: PoliciesEntityFieldLabelType.Status,
              render: (row) => row?.status,
              isSortable: true,
            },
            {
              id: PoliciesEntityFieldIdType.DocumentsLink,
              label: PoliciesEntityFieldLabelType.DocumentsLink,
              render: (row) => (
                <>
                  {row.documentLink ? (
                    <ConvergeButton
                      startIcon={<FolderCopyIcon />}
                      label={'Documents'}
                      href={row.documentLink}
                      target="_blank"
                    />
                  ) : (
                    'N/A'
                  )}
                </>
              ),
              isSortable: true,
            },
          ]}
          rows={policies}
          enablePagination
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          totalRows={data?.policies?.pageInfo.totalResults || 0}
          handleChangeRowsPerPage={(newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setCurrentPage(0);
          }}
          handleChangePage={setCurrentPage}
          emptyRowMessage={t(
            'entities.policies.grid.emptyMessage',
            'No policies found'
          )}
        />
      </PaperLayout>
    </>
  );
};
